import React, { useRef, useEffect, useState } from 'react';
import './Marquee.scss';

interface MarqueeProps {
  children: React.ReactNode;
  className?: string;
  title?: string;
}

export const Marquee: React.FC<MarqueeProps> = ({ children, className, title }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [scrollDist, setScrollDist] = useState(0);

  useEffect(() => {
    const checkOverflow = () => {
      const container = containerRef.current;
      const text = textRef.current;
      if (!container || !text) return;

      const cWidth = container.getBoundingClientRect().width;
      const sWidth = text.getBoundingClientRect().width;

      // Only evaluate if container is properly rendered (width > 20px)
      // Allow a 3px threshold so rounding errors / font hints don't cause false marquees
      if (cWidth > 20 && sWidth > cWidth + 3) {
        const overflow = Math.round(sWidth - cWidth);
        setIsOverflowing(true);
        // Add an 18px buffer so the trailing text comfortably clears the edge fade mask and is fully visible
        setScrollDist(overflow + 18);
      } else {
        setIsOverflowing(false);
        setScrollDist(0);
      }
    };

    checkOverflow();

    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined' && containerRef.current) {
      ro = new ResizeObserver(() => {
        checkOverflow();
      });
      ro.observe(containerRef.current);
      if (textRef.current) {
        ro.observe(textRef.current);
      }
    }

    window.addEventListener('resize', checkOverflow);
    const timeoutId = setTimeout(checkOverflow, 150);

    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', checkOverflow);
      clearTimeout(timeoutId);
    };
  }, [children]);

  return (
    <div
      className={`marquee-container ${className || ''} ${isOverflowing ? 'is-overflowing' : ''}`}
      ref={containerRef}
      title={title}
      style={{ '--scroll-dist': `-${scrollDist}px`, minWidth: 0 } as React.CSSProperties}
    >
      <div className={`marquee-content ${isOverflowing ? 'scroll' : ''}`}>
        <span className="marquee-text" ref={textRef}>
          {children}
        </span>
      </div>
    </div>
  );
};

